import React, { useEffect, useMemo, useRef, useState } from "react";

const IterativeSolversAnimation = ({ isActive, size = 320, className = "" }) => {
  // Match your icon's coordinates
  const viewBox = "-26 -10 160 120";

  // Points: x0 -> x1 -> x2 -> x (final)
  const points = useMemo(
    () => [
      { x: 10, y: 120 },     // x0
      { x: 45, y: 105 },     // x1
      { x: 25, y: 60 },      // x2
      { x: 51.5, y: 50.5 },  // x (final)
    ],
    []
  );

  // Build per-segment paths and lengths
  const segments = useMemo(() => {
    const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
    const segs = [];
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i];
      const b = points[i + 1];
      segs.push({
        d: `M ${a.x} ${a.y} L ${b.x} ${b.y}`,
        len: dist(a, b),
        start: a,
        end: b,
      });
    }
    return segs;
  }, [points]);

  // Animation state
  const [segIndex, setSegIndex] = useState(0);   // which segment is animating
  const [progress, setProgress] = useState(0);   // 0..1 within current segment

  // Timing (tweak to taste)
  const SEGMENT_DURATION_MS = 900;   // how long to draw each segment
  const BETWEEN_SEGMENTS_PAUSE = 300; // pause after each segment

  // Animation loop
  const rafRef = useRef(null);
  const t0Ref = useRef(0);
  const pausingRef = useRef(false);
  const pausedAtRef = useRef(0);

  useEffect(() => {
    // Reset when not active
    if (!isActive || segments.length === 0) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setSegIndex(0);
      setProgress(0);
      pausingRef.current = false;
      return;
    }

    const step = (t) => {
      if (t0Ref.current === 0) t0Ref.current = t;

      if (pausingRef.current) {
        // Handle short pause between segments
        if (t - pausedAtRef.current >= BETWEEN_SEGMENTS_PAUSE) {
          pausingRef.current = false;
          t0Ref.current = t; // restart duration timing for next segment
        }
      } else {
        const elapsed = t - t0Ref.current;
        const p = Math.min(1, elapsed / SEGMENT_DURATION_MS);
        setProgress(p);

        if (p >= 1) {
          // Move to next segment (or loop)
          if (segIndex < segments.length - 1) {
            setSegIndex((i) => i + 1);
            setProgress(0);
            pausingRef.current = true;
            pausedAtRef.current = t;
          } else {
            // Finished all segments; brief pause then loop
            setSegIndex(0);
            setProgress(0);
            pausingRef.current = true;
            pausedAtRef.current = t;
          }
          t0Ref.current = t;
        }
      }

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      t0Ref.current = 0;
    };
  }, [isActive, segments.length, segIndex]);

  const stroke = "#f3f4f6";

  const maxWidth = size ? (typeof size === "number" ? `${size}px` : size) : undefined;
  const baseClasses = "relative overflow-hidden rounded-lg bg-[#38bdf8] aspect-square w-full";

  return (
    <div
      className={`${baseClasses}${className ? ` ${className}` : ""}`}
      style={{ maxWidth, aspectRatio: "1 / 1" }}
    >
      <svg
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-full"
      >
        {/* Concentric ellipses (level sets), matching your icon */}
        {[
          [20, 15],
          [50, 37],
          [80, 60],
        ].map(([rx, ry], i) => (
          <ellipse
            key={i}
            cx="60"
            cy="35"
            rx={rx}
            ry={ry}
            transform="rotate(48 38 32)"
            fill="none"
            stroke={stroke}
            strokeWidth="8"
            opacity={0.9}
          />
        ))}

        {/* Already-completed segments */}
        {segments.map((s, i) =>
          i < segIndex ? (
            <path
              key={`done-${i}`}
              d={s.d}
              fill="none"
              stroke={stroke}
              strokeWidth="10"
              strokeLinecap="round"
            />
          ) : null
        )}

        {/* Current animating segment */}
        {segments[segIndex] && (
          <AnimatedSegment
            d={segments[segIndex].d}
            length={segments[segIndex].len}
            progress={progress}
            stroke={stroke}
          />
        )}

        {/* Points (x0, x1, x2, x) */}
        {/* x0 */}
        <circle cx={points[0].x} cy={points[0].y} r="5.5" fill={stroke} />
        {/* Intermediate points */}
        <circle cx={points[1].x} cy={points[1].y} r="4.8" fill={stroke} />
        <circle cx={points[2].x} cy={points[2].y} r="4.8" fill={stroke} />
        {/* Final x */}
        <circle cx={points[3].x} cy={points[3].y} r="5.5" fill={stroke} />
      </svg>
    </div>
  );
};

// Renders a path whose visible length grows with `progress` (0..1)
const AnimatedSegment = ({ d, length, progress, stroke }) => {
  // Dash pattern that reveals from start to end
  const dashArray = length;
  const dashOffset = (1 - progress) * length;

  return (
    <path
      d={d}
      fill="none"
      stroke={stroke}
      strokeWidth="10"
      strokeLinecap="round"
      strokeDasharray={dashArray}
      strokeDashoffset={dashOffset}
    />
  );
};

export default IterativeSolversAnimation;
